import { browserHistory, RedirectFunction, RouterState } from "react-router";
import { syncHistoryWithStore as syncHistoryWithStoreMOBX } from "mobx-react-router";
import { rootStore } from "#stores/index";
import { clearQueryClient } from "#structure/providers/react-query";
import {
	isEventRouteAllowedWhenExpired,
	isPlaceRouteAllowedWhenExpired,
} from "./expired-place-routes";

export const history = syncHistoryWithStoreMOBX(browserHistory, rootStore.routerStore);

async function setPlaceAndEventContext(nextState: RouterState): Promise<void> {
	if (nextState.params.placeId) {
		await rootStore.placeStore.setCurrentPlace(nextState.params.placeId);
	} else {
		await rootStore.placeStore.unsetCurrentPlace();
	}

	if (nextState.params.eventId) {
		await rootStore.eventStore.setCurrentEvent(nextState.params.eventId);
	} else {
		await rootStore.eventStore.unsetCurrentEvent();
	}
}

function checkPlaceExpired(nextState: RouterState): boolean {
	const isPlaceExpired = rootStore.placeStore.place?.isExpired ?? false;

	if (!isPlaceExpired || !nextState.params.placeId) {
		return false;
	}

	const { placeId, eventId } = nextState.params;
	const pathname = nextState.location.pathname;

	const isPlaceRoute = !eventId;
	const isEventRoute = !!eventId;

	if (isPlaceRoute) {
		const isAllowed = isPlaceRouteAllowedWhenExpired(placeId, pathname);

		if (!isAllowed) {
			rootStore.routerStore?.replace(`/place/${placeId}/business-intelligence`);
			return true;
		}
	}

	if (isEventRoute) {
		const isAllowed = isEventRouteAllowedWhenExpired(placeId, eventId, pathname);

		if (!isAllowed) {
			rootStore.routerStore?.replace(`/place/${placeId}/event/${eventId}`);
			return true;
		}
	}

	return false;
}

/**
 * Verifica se há restrições de vendor e redireciona para a rota correta
 * @param nextState - Estado da rota atual
 * @returns true se houver restrições de vendor e a rota foi redirecionada, false caso contrário
 */
function checkVendorRestrictionRedirect(nextState: RouterState): boolean {
	const { placeId, eventId } = nextState.params;

	if (!eventId || !placeId) {
		return false;
	}

	const hasVendorRestrictions = !!rootStore.authStore.currentEmployee?.vendors?.length;

	if (!hasVendorRestrictions) {
		return false;
	}

	return false;
}

export async function requireAuth(
	nextState: RouterState,
	_replace: RedirectFunction,
	callback: () => void | undefined,
) {
	if (!rootStore.authStore.currentEmployee) {
		const currentRoute = history.getCurrentLocation();
		if (currentRoute.pathname !== "/login") {
			sessionStorage.setItem("redirectAfterLogin", currentRoute.pathname);
		}
		clearQueryClient();
		rootStore.routerStore?.push("/login");
	} else {
		await rootStore.organizationStore.fetchCurrentOrganization();
		await setPlaceAndEventContext(nextState);
		checkPlaceExpired(nextState);
		checkVendorRestrictionRedirect(nextState);
	}
	callback();
}

export function requireNotAuth() {
	if (rootStore.authStore.currentEmployee) {
		rootStore.routerStore.push("/");
	}
}

export async function updatePlaceAndEvent(
	_prevState: RouterState,
	nextState: RouterState,
	_replace: RedirectFunction,
	callback: () => void | undefined,
) {
	await setPlaceAndEventContext(nextState);
	checkPlaceExpired(nextState);
	checkVendorRestrictionRedirect(nextState);
	callback();
}
